import { FEATURES, type Feature } from '@/features/feature-flags';
import { isMac, isWindows } from '@/utils/capabilities';

import {
  supportedOnThisDevice,
  type ConfigRequirement,
  type IntegrationPlatform,
  type McpPermission,
} from './integrations.types';

import type {
  ServerSetup,
  ServerTransport,
} from '@littlebird/axon-mcp/contract';

/**
 * CPU architectures a bundled server ships a binary for. Separate from
 * `platforms` because a driver can support macOS while shipping only one
 * slice — `runtimes.manifest.json` is the source of truth for which.
 */
export type TemplateArch = 'arm64' | 'x64';

export interface BundledMcpTemplate {
  id: string;
  name: string;
  description: string;
  transport: Extract<ServerTransport, { kind: 'bundled' }>;
  setup?: ServerSetup;
  categories: string[];
  platforms: readonly IntegrationPlatform[];
  /** Unset means every arch; a list means only those. */
  archs?: readonly TemplateArch[];
  requirements?: ConfigRequirement[];
  requiredPermissions?: readonly McpPermission[];
  featureFlag?: Feature;
  /**
   * Shown while connecting, for servers whose "runs on your machine" shape
   * changes what the user can expect of them. Informational, not a warning, so
   * keep every line factual and free of reassurance the code can't guarantee.
   */
  deviceNote?: readonly string[];
  /** The "Littlebird will be able to" lines of the connect dialog and settings tab */
  capabilities?: readonly string[];
}

const BUNDLED_MCP_TEMPLATES: readonly BundledMcpTemplate[] = [
  {
    id: 'apple-mail',
    name: 'Apple Mail',
    description: 'Search your mail history, indexed privately on your Mac.',
    transport: { kind: 'bundled', serverId: 'apple-mail' },
    setup: {
      label: 'Build search index',
      env: { MAIL_INDEX_ONCE: '1' },
      // the verdict line is authored by our patch and cut before any path or quote
      detailInTelemetry: true,
    },
    categories: ['apple'],
    platforms: ['macos'],
    requiredPermissions: ['fullDiskAccess'],
  },
  {
    id: 'computer-use',
    name: 'Computer Use',
    // three-way: the browser build (isMac and isWindows both false) must not
    // show platform-specific copy — the web grid renders this card too
    description: isMac()
      ? 'See the screen and control apps on your Mac. Uses the Accessibility and Screen Recording permissions Littlebird already has.'
      : isWindows()
        ? 'See the screen and control apps on your PC.'
        : 'See the screen and control apps on your computer.',
    transport: { kind: 'bundled', serverId: 'computer-use' },
    categories: ['productivity'],
    platforms: ['macos', 'windows'],
    // the driver runs under Littlebird's bundle id, so macOS checks the app's grants
    requiredPermissions: isMac() ? ['accessibility', 'screenRecording'] : [],
    featureFlag: FEATURES.AXON_COMPUTER_USE,
    capabilities: [
      'See what’s on your screen and in your windows',
      'Click, scroll and type in the apps you can see',
      'Open apps by name',
    ],
    deviceNote: isWindows()
      ? [
          'Computer Use can’t inspect or control password managers, Windows password prompts or Littlebird itself, though they can show up in a view of the whole screen.',
          'Terminals and command prompts can be read and clicked, but never typed or pasted into.',
        ]
      : [
          'Computer Use can’t inspect or control password managers, Keychain Access, password prompts or Littlebird itself, though they can show up in a view of the whole screen.',
          'Terminals and scripting apps can be read and clicked, but never typed or pasted into.',
        ],
  },
  {
    // Signs in on this device against Microsoft Graph, so tenants that refuse
    // third-party Entra apps never see one. Upstream ships a Windows build too;
    // it stays macOS-only until we publish that slice to the CDN.
    id: 'outlook-local',
    name: 'Outlook',
    description: 'Read and manage your Outlook mail and calendar.',
    transport: { kind: 'bundled', serverId: 'outlook-local' },
    categories: ['productivity'],
    featureFlag: FEATURES.AXON_OUTLOOK_LOCAL,
    platforms: ['macos'],
    archs: ['arm64'],
    deviceNote: [
      'Littlebird connects to Outlook through this device, like the Outlook app itself, and stores your Microsoft sign-in in your Mac’s keychain.',
    ],
  },
];

function currentTemplateArch(): TemplateArch | undefined {
  const arch = window.systemInfo?.arch;
  return arch === 'arm64' || arch === 'x64' ? arch : undefined;
}

function runsOnThisArch(template: BundledMcpTemplate): boolean {
  if (!template.archs) return true;
  const arch = currentTemplateArch();
  return arch !== undefined && template.archs.includes(arch);
}

export function getBundledMcpTemplates(
  includeUnsupportedPlatforms = false
): readonly BundledMcpTemplate[] {
  return includeUnsupportedPlatforms
    ? BUNDLED_MCP_TEMPLATES
    : BUNDLED_MCP_TEMPLATES.filter(
        (template) =>
          supportedOnThisDevice(template.platforms) && runsOnThisArch(template)
      );
}

export function getBundledMcpTemplate(
  id: string | undefined
): BundledMcpTemplate | undefined {
  return BUNDLED_MCP_TEMPLATES.find((template) => template.id === id);
}
